import type { ToolsState, ToolState, ToolName, LegacyToolConfigItem } from '@/containers/Tools/types'
import {
  SET_TOOL_CONFIG,
  SET_TOOL_VISIBLE,
  SET_TOOL_COLLAPSED,
  SET_TOOL_POSITION,
  SET_LAYER_SETTINGS_LAYER,
  SET_GROUP_SETTINGS_GROUP,
  SET_MAPVIEW_SETTINGS_GROUP,
  SET_DRAW_TARGET_LAYER,
  CLOSE_LAYER_SETTINGS,
  CLOSE_GROUP_SETTINGS,
  INITIALIZE_TOOLS_STATE,
} from '@/actions/types'
import { defaultToolConfig } from '@/config/toolConfig'

// =============================================================================
// Migration: Convert legacy array format to normalized object
// =============================================================================

/**
 * Converts legacy toolConfig array to normalized ToolsState object
 */
export const normalizeToolConfig = (
  legacyConfig: LegacyToolConfigItem[] | null
): ToolsState['tools'] => {
  if (!legacyConfig || !Array.isArray(legacyConfig)) {
    // Initialize from default config if no legacy state
    return normalizeToolConfig(defaultToolConfig)
  }

  const normalized: ToolsState['tools'] = {}

  legacyConfig.forEach(tool => {
    if (tool.name) {
      normalized[tool.name as ToolName] = {
        name: tool.name as ToolName,
        title: tool.title || tool.name,
        icon: tool.icon || 'cog',
        toc: tool.toc ?? false,
        visible: tool.visible ?? false,
        ...(tool.collapsed !== undefined && { collapsed: tool.collapsed }),
        ...(tool.positionX !== undefined && { positionX: tool.positionX }),
        ...(tool.positionY !== undefined && { positionY: tool.positionY }),
        ...(tool.layer !== undefined && { layer: tool.layer }),
        ...(tool.group !== undefined && { group: tool.group }),
        ...(tool.mapViewGroup !== undefined && { mapViewGroup: tool.mapViewGroup }),
        ...(tool.targetLayer !== undefined && { targetLayer: tool.targetLayer }),
        ...(tool.fixedTop !== undefined && { fixedTop: tool.fixedTop }),
        ...(tool.minWidth !== undefined && { minWidth: tool.minWidth }),
        ...(tool.minHeight !== undefined && { minHeight: tool.minHeight }),
      } as ToolState
    }
  })

  return normalized
}


// =============================================================================
// Normalized Tools Reducer
// =============================================================================

const initialState: ToolsState = {
  tools: normalizeToolConfig(defaultToolConfig),
}

export default function ToolsReducer(
  state: ToolsState = initialState,
  action: any
): ToolsState {
  switch (action.type) {
    case SET_TOOL_CONFIG: {
      // Ensure payload is normalized format
      if (action.payload && typeof action.payload === 'object' && 'tools' in action.payload) {
        return action.payload
      }
      // If invalid format, return current state or initialize from default
      return state.tools ? state : {
        tools: normalizeToolConfig(defaultToolConfig),
      }
    }

    // Initialize from default config
    case INITIALIZE_TOOLS_STATE: {
      return {
        tools: normalizeToolConfig(defaultToolConfig),
      }
    }

    // Set tool visibility
    case SET_TOOL_VISIBLE: {
      const { toolName, visible } = action.payload
      const tool = state.tools[toolName as ToolName]
      if (!tool) return state

      return {
        tools: {
          ...state.tools,
          [toolName]: {
            ...tool,
            visible,
          },
        },
      }
    }

    // Set tool collapsed state
    case SET_TOOL_COLLAPSED: {
      const { toolName, collapsed } = action.payload
      const tool = state.tools[toolName as ToolName]
      if (!tool || !('collapsed' in tool)) return state

      return {
        tools: {
          ...state.tools,
          [toolName]: {
            ...tool,
            collapsed,
          },
        },
      }
    }

    // Set tool position
    case SET_TOOL_POSITION: {
      const { toolName, positionX, positionY } = action.payload
      const tool = state.tools[toolName as ToolName]
      if (!tool) return state

      // Only update position if tool supports it
      const updatedTool = { ...tool }
      if ('positionX' in updatedTool) {
        updatedTool.positionX = positionX ?? updatedTool.positionX
      }
      if ('positionY' in updatedTool) {
        updatedTool.positionY = positionY ?? updatedTool.positionY
      }

      return {
        tools: {
          ...state.tools,
          [toolName]: updatedTool,
        },
      }
    }

    // Set LayerSettings layer
    case SET_LAYER_SETTINGS_LAYER: {
      const { layer } = action.payload
      const tool = state.tools.LayerSettings
      if (!tool || tool.name !== 'LayerSettings') return state

      return {
        tools: {
          ...state.tools,
          LayerSettings: {
            ...tool,
            layer,
            visible: layer !== null,
          },
        },
      }
    }

    // Close LayerSettings
    case CLOSE_LAYER_SETTINGS: {
      const tool = state.tools.LayerSettings
      if (!tool || tool.name !== 'LayerSettings') return state

      return {
        tools: {
          ...state.tools,
          LayerSettings: {
            ...tool,
            visible: false,
            layer: null,
          },
        },
      }
    }

    // Set GroupSettings group
    case SET_GROUP_SETTINGS_GROUP: {
      const { group } = action.payload
      const tool = state.tools.GroupSettings
      if (!tool || tool.name !== 'GroupSettings') return state

      return {
        tools: {
          ...state.tools,
          GroupSettings: {
            ...tool,
            group,
            visible: group !== null,
          },
        },
      }
    }

    // Close GroupSettings
    case CLOSE_GROUP_SETTINGS: {
      const tool = state.tools.GroupSettings
      if (!tool || tool.name !== 'GroupSettings') return state

      return {
        tools: {
          ...state.tools,
          GroupSettings: {
            ...tool,
            visible: false,
            group: null,
          },
        },
      }
    }

    // Set MapViewSettings group
    case SET_MAPVIEW_SETTINGS_GROUP: {
      const { mapViewGroup } = action.payload
      const tool = state.tools.MapViewSettings
      if (!tool || tool.name !== 'MapViewSettings') return state

      return {
        tools: {
          ...state.tools,
          MapViewSettings: {
            ...tool,
            mapViewGroup,
            visible: mapViewGroup !== null,
          },
        },
      }
    }

    // Set Draw target layer
    case SET_DRAW_TARGET_LAYER: {
      const { targetLayer } = action.payload
      const tool = state.tools.Draw
      if (!tool || tool.name !== 'Draw') return state

      return {
        tools: {
          ...state.tools,
          Draw: {
            ...tool,
            targetLayer,
          },
        },
      }
    }

    default:
      return state
  }
}
